/**
 * The character state: a flat record of numbers, double-buffered by the caller.
 * `x, y` is the feet point (the bottom of the collision box); everything else on
 * the body (capsule center, shoulders, hands) is derived from it and the tuck
 * amount so the collision shape, the pendulum, and the drawn pose always agree.
 */

import { S } from "./types";

export class SimState {
  tick = 0;
  /** Feet position, m. */
  x = 0;
  y = 0;
  /** Velocity, m/s. On a bar this is the tangential velocity plus any animated ladder lift. */
  vx = 0;
  vy = 0;
  /** Acceleration applied this tick (m/s^2), for the renderer's lean and the tests. */
  ax = 0;
  ay = 0;
  /** +1 or -1. */
  facing = 1;
  /** Torso orientation and angular velocity, rad and rad/s. World space: positive leans the head toward +x. */
  phi = 0;
  phiDot = 0;
  /** Moment of inertia from the tuck and the reach (0 until the first tick computes it). */
  inertia = 0;
  /** Pump lean off the arm line while attached, -1..1 of barLeanClamp. */
  barLean = 0;
  /** Attitude at the grab, eased onto the arm line over the attach ticks. */
  attachPhi0 = 0;
  /** 0..1, continuous. */
  tuck = 0;
  /** The smallest tuck that fits under the current ceiling (0 in the open). */
  tuckFloor = 0;
  reach = 0;
  pull = 0;
  /** 1 when standing on a solid. */
  grounded = 0;
  /** Bit field of collisions this tick: 1 landed, 2 head hit, 4 wall hit, 8 corner nudge. */
  collided = 0;
  coyote = 0;
  buffer = 0;
  /** 1 while the jump cut is still available on this jump. */
  jumpCutArmed = 0;
  state: number = S.Idle;
  /** Ticks spent in the current state. */
  stateTicks = 0;
  /** Distance travelled along the ground, m (drives the run cycle). */
  groundDistance = 0;

  // Bar
  /** Attached bar id or -1. */
  barId = -1;
  theta = 0;
  omega = 0;
  L = 1;
  Lrest = 1;
  /** Attach ease ticks remaining. */
  attachLeft = 0;
  attachTotal = 0;
  attachL0 = 0;
  /** Tick (fractional) of the last bottom crossing, or -1e9. */
  lastCrossTime = -1e9;
  lastBarId = -1;
  lockout = 0;
  /** 1 once the hands have left the last bar's reach radius. */
  leftRadius = 0;
  lastPumpGrade = 0;
  lastPumpOffsetMs = 0;
  /** Tick of the last pump, for the renderer's flash. */
  lastPumpTick = -1e9;
  /** Tick (fractional) of the swing's last apex (a turn of omega) while attached, or -1e9. */
  lastApexTime = -1e9;

  // Salmon ladder
  /** The ladder whose bar the player holds (-1 none) and the rung that bar sits on; it drops to the bottom on a release. */
  ladderId = -1;
  ladderRung = 0;
  lastLadderGrade = 0;
  /** Consecutive climbs without skipping a bottom crossing (an apex for legacy tapes). */
  ladderChain = 0;
  /** 1 while the bar/body rise to the next rung (legacy tapes use the attach ease). */
  popping = 0;
  /** Animated bar height above barId until the next rung seats (current ladder rules only). */
  ladderLiftY = 0;
  ladderLiftTick = 0;
  /** Vertical speed of the moving bar, carried into a mid-climb dismount. */
  ladderLiftVelocity = 0;
  /** Crossing serial and the crossing already rewarded, preventing repeated ducks in one window. */
  ladderCrossings = 0;
  ladderDuckBottom = -1;

  // Link and grades
  /** Ticks the arms have been out while not attached (the catch setup). */
  armsOutTicks = 0;
  /** 1 from a clean release (the forward swing, no backswing or dead hang) until the next grab, which then links. */
  linkOpen = 0;
  /** +1 or -1: the swing direction a link must keep on this bar. */
  linkDir = 1;
  /** 1 once this attached episode can no longer link (a backswing or a dead hang). */
  linkSpoiled = 0;
  /** Ticks the swing has been slower than hangOmega while attached. */
  hangTicks = 0;
  /** Linked bars in the current run, and the best run since the last restart. */
  linkRun = 0;
  linkBest = 0;
  lastCatchGrade = 0;
  lastReleaseGrade = 0;
  lastRollGrade = 0;
  /** Tick of the last Graded event and its kind and grade, for popups that sample state. */
  lastGradeTick = -1e9;
  lastGradeKind = 0;
  lastGrade = 0;
  /** Hit-stop ticks remaining; while positive the world is frozen. */
  freezeTicks = 0;

  // Hurdles and slide
  /** Box a vault or a hop is going over, or -1. */
  plantBox = -1;
  /** PlantPhase: 0 none, 1 taking off toward the box, 2 the hands holding the body over it, 3 let go. */
  planted = 0;
  /** 1 when the hands take part (a vault), 0 for the hop over a knee-high box. */
  plantHands = 0;
  /** Horizontal speed the plant eases toward, m/s (signed). */
  plantTargetVx = 0;
  /** Ticks since the plant, for the retention ease. */
  plantTicks = 0;
  /** Box a vault press is armed for (-1 none) and the grade the press earned. */
  vaultArmBox = -1;
  vaultArmGrade = 0;
  lastVaultGrade = 0;
  lastSlideGrade = 0;
  /** Pipe already rewarded on this approach (-1 none), and the direction of that slide. */
  slideBoostPipe = -1;
  slideBoostDir = 1;
  /** A perfect press is waiting for the body to fit below its pipe before adding speed. */
  slideBoostReady = 0;
  /** 1 when the coyote time comes from a slide, so a jump in it is a slide jump. */
  slideCoyote = 0;

  // Ledge (DESIGN.md 4.9)
  /** Solid whose top corner the hands hold (-1 none), and which way its wall is from the body (+1 at +x). */
  ledgeSolid = -1;
  ledgeDir = 1;
  /** Horizontal speed at the grab, m/s: the speed the mantle's grade keeps over the edge. */
  ledgeEntrySpeed = 0;
  /** The feet at the grab, eased onto the hang over the attach ticks. */
  ledgeGrabX = 0;
  ledgeGrabY = 0;
  /** The last ledge let go of or mantled, and the ticks before it can be taken again. */
  lastLedge = -1;
  ledgeLockout = 0;
  /** 1 while the plant is a mantle: the hands pressing the body up the face and over the ledge. */
  mantling = 0;
  lastMantleGrade = 0;

  // Landing
  /** Highest height of the mass since the last contact (the drop a touchdown reads). */
  apexY = 0;
  /** 1 while rolling: the body turns over its rolling contact and the stick is ignored. */
  tumbling = 0;
  /** Direction of the roll (+1 or -1) and how far it has turned from upright, radians. */
  rollDir = 1;
  rollTurn = 0;
  impactSpeed = 0;
  ticksSinceLand = 1e9;
  ticksSinceRelease = 1e9;
  /** Ticks on a bar with no push, tuck, or pull; drives the idle damping of the swing. */
  idleTicks = 0;

  // Arms (derived every tick)
  shoulderX = 0;
  shoulderY = 0;
  handX = 0;
  handY = 0;
  reachDirX = 0;
  reachDirY = 1;
  reachRadius = 0;
  /** 1 when reach is at or above the grip threshold. */
  armsOut = 0;
  /** Bar targeted by grip assist, or -1. */
  assistBar = -1;

  // Course progress
  respawnIndex = 0;
  markIndex = 0;
  failTicks = 0;

  // Timed course (not hashed: a run's times are events, and the gym has none)
  /** Run phase, a `Run` value. */
  run = 0;
  /** Start line crossing of the current run, fractional tick. */
  runStart = 0;
  /** Time of the finished run, s (0 until it finishes). */
  runTime = 0;
  /** Falls into water during the current run. */
  runFalls = 0;

  // Previous inputs for edge detection
  prevJump = 0;
  prevTuck = 0;
  prevRestart = 0;
  prevPause = 0;
  /** 1 while the pull is held past the ladder's pull threshold. */
  prevPull = 0;

  copyFrom(s: SimState): void {
    this.tick = s.tick;
    this.x = s.x;
    this.y = s.y;
    this.vx = s.vx;
    this.vy = s.vy;
    this.ax = s.ax;
    this.ay = s.ay;
    this.facing = s.facing;
    this.phi = s.phi;
    this.phiDot = s.phiDot;
    this.inertia = s.inertia;
    this.barLean = s.barLean;
    this.attachPhi0 = s.attachPhi0;
    this.tuck = s.tuck;
    this.tuckFloor = s.tuckFloor;
    this.reach = s.reach;
    this.pull = s.pull;
    this.grounded = s.grounded;
    this.collided = s.collided;
    this.coyote = s.coyote;
    this.buffer = s.buffer;
    this.jumpCutArmed = s.jumpCutArmed;
    this.state = s.state;
    this.stateTicks = s.stateTicks;
    this.groundDistance = s.groundDistance;
    this.barId = s.barId;
    this.theta = s.theta;
    this.omega = s.omega;
    this.L = s.L;
    this.Lrest = s.Lrest;
    this.attachLeft = s.attachLeft;
    this.attachTotal = s.attachTotal;
    this.attachL0 = s.attachL0;
    this.lastCrossTime = s.lastCrossTime;
    this.lastBarId = s.lastBarId;
    this.lockout = s.lockout;
    this.leftRadius = s.leftRadius;
    this.lastPumpGrade = s.lastPumpGrade;
    this.lastPumpOffsetMs = s.lastPumpOffsetMs;
    this.lastPumpTick = s.lastPumpTick;
    this.lastApexTime = s.lastApexTime;
    this.ladderId = s.ladderId;
    this.ladderRung = s.ladderRung;
    this.lastLadderGrade = s.lastLadderGrade;
    this.ladderChain = s.ladderChain;
    this.popping = s.popping;
    this.ladderLiftY = s.ladderLiftY;
    this.ladderLiftTick = s.ladderLiftTick;
    this.ladderLiftVelocity = s.ladderLiftVelocity;
    this.ladderCrossings = s.ladderCrossings;
    this.ladderDuckBottom = s.ladderDuckBottom;
    this.armsOutTicks = s.armsOutTicks;
    this.linkOpen = s.linkOpen;
    this.linkDir = s.linkDir;
    this.linkSpoiled = s.linkSpoiled;
    this.hangTicks = s.hangTicks;
    this.linkRun = s.linkRun;
    this.linkBest = s.linkBest;
    this.lastCatchGrade = s.lastCatchGrade;
    this.lastReleaseGrade = s.lastReleaseGrade;
    this.lastRollGrade = s.lastRollGrade;
    this.lastGradeTick = s.lastGradeTick;
    this.lastGradeKind = s.lastGradeKind;
    this.lastGrade = s.lastGrade;
    this.freezeTicks = s.freezeTicks;
    this.plantBox = s.plantBox;
    this.planted = s.planted;
    this.plantHands = s.plantHands;
    this.plantTargetVx = s.plantTargetVx;
    this.plantTicks = s.plantTicks;
    this.vaultArmBox = s.vaultArmBox;
    this.vaultArmGrade = s.vaultArmGrade;
    this.lastVaultGrade = s.lastVaultGrade;
    this.lastSlideGrade = s.lastSlideGrade;
    this.slideBoostPipe = s.slideBoostPipe;
    this.slideBoostDir = s.slideBoostDir;
    this.slideBoostReady = s.slideBoostReady;
    this.slideCoyote = s.slideCoyote;
    this.ledgeSolid = s.ledgeSolid;
    this.ledgeDir = s.ledgeDir;
    this.ledgeEntrySpeed = s.ledgeEntrySpeed;
    this.ledgeGrabX = s.ledgeGrabX;
    this.ledgeGrabY = s.ledgeGrabY;
    this.lastLedge = s.lastLedge;
    this.ledgeLockout = s.ledgeLockout;
    this.mantling = s.mantling;
    this.lastMantleGrade = s.lastMantleGrade;
    this.apexY = s.apexY;
    this.tumbling = s.tumbling;
    this.rollDir = s.rollDir;
    this.rollTurn = s.rollTurn;
    this.impactSpeed = s.impactSpeed;
    this.ticksSinceLand = s.ticksSinceLand;
    this.ticksSinceRelease = s.ticksSinceRelease;
    this.idleTicks = s.idleTicks;
    this.shoulderX = s.shoulderX;
    this.shoulderY = s.shoulderY;
    this.handX = s.handX;
    this.handY = s.handY;
    this.reachDirX = s.reachDirX;
    this.reachDirY = s.reachDirY;
    this.reachRadius = s.reachRadius;
    this.armsOut = s.armsOut;
    this.assistBar = s.assistBar;
    this.respawnIndex = s.respawnIndex;
    this.markIndex = s.markIndex;
    this.failTicks = s.failTicks;
    this.run = s.run;
    this.runStart = s.runStart;
    this.runTime = s.runTime;
    this.runFalls = s.runFalls;
    this.prevJump = s.prevJump;
    this.prevTuck = s.prevTuck;
    this.prevRestart = s.prevRestart;
    this.prevPause = s.prevPause;
    this.prevPull = s.prevPull;
  }
}

/** Keys hashed by hashState, in order. Kept explicit so the hash is stable across refactors. */
export const HASHED_KEYS = [
  "tick",
  "x",
  "y",
  "vx",
  "vy",
  "facing",
  "phi",
  "phiDot",
  "tuck",
  "reach",
  "grounded",
  "coyote",
  "buffer",
  "state",
  "barId",
  "theta",
  "omega",
  "L",
  "respawnIndex",
  "armsOutTicks",
  "linkRun",
  "linkBest",
  "freezeTicks",
  "tumbling",
  "rollTurn",
  "apexY",
  "plantBox",
  "planted",
  "ledgeSolid",
  "mantling",
] as const satisfies readonly (keyof SimState)[];

/** New ladder continuation state; legacy recordings keep all of these at their initial values. */
const LADDER_HASHED_KEYS = [
  "popping",
  "ladderLiftY",
  "ladderLiftTick",
  "ladderLiftVelocity",
  "ladderCrossings",
  "ladderDuckBottom",
] as const satisfies readonly (keyof SimState)[];

const hashF64 = new Float64Array(1);
const hashBytes = new Uint8Array(hashF64.buffer);

/** FNV-1a over the raw IEEE bytes of the hashed fields. Bit-exact across engines. */
export function hashState(s: SimState, seed = 0x811c9dc5): number {
  let h = seed >>> 0;
  for (const k of HASHED_KEYS) {
    hashF64[0] = s[k] as number;
    for (let i = 0; i < 8; i++) {
      h ^= hashBytes[i]!;
      h = Math.imul(h, 0x01000193) >>> 0;
    }
  }
  // Preserve historical hashes byte-for-byte while the new ladder continuation state is absent.
  if (
    s.ladderLiftY !== 0 ||
    s.ladderLiftTick !== 0 ||
    s.ladderLiftVelocity !== 0 ||
    s.ladderCrossings !== 0 ||
    s.ladderDuckBottom !== -1
  ) {
    for (const k of LADDER_HASHED_KEYS) {
      hashF64[0] = s[k];
      for (let i = 0; i < 8; i++) {
        h ^= hashBytes[i]!;
        h = Math.imul(h, 0x01000193) >>> 0;
      }
    }
  }
  return h >>> 0;
}

export function hashStateHex(s: SimState): string {
  return hashState(s).toString(16).padStart(8, "0");
}

/** All numeric fields, for tests (NaN checks, copy coverage). */
export function stateKeys(s: SimState): string[] {
  return Object.keys(s).filter((k) => typeof (s as unknown as Record<string, unknown>)[k] === "number");
}
